import React, { useEffect, useMemo, useState } from 'react';

import { useSnackbar } from 'notistack';
import { useParams } from 'react-router-dom';

import {
  Box,
  Button,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  ImageList,
  ImageListItem,
  ImageListItemBar,
  InputBase,
  Stack,
  Typography,
} from '@mui/material';

import Iconify from '../../components/Iconify';
import Page from '../../components/Page';
import { MenuService } from '../../Services/menu.service';
import { useLoadingContext } from '../../context/loadingContext';

const MenuDetail = () => {
  const { id } = useParams();
  const { enqueueSnackbar } = useSnackbar();
  const [menuData, setMenuData] = useState();
  const [canEdit, setCanEdit] = useState(false);
  const [images, setImages] = useState([]);
  const [show, setShow] = useState(false);
  const { handleStartLoading, handleStopLoading } = useLoadingContext();

  useEffect(() => {
    getMenuById().finally(handleStopLoading);
  }, []);

  const titleData = useMemo(() => {
    return { _id: menuData?._id, menuName: menuData?.menuName };
  }, [menuData]);

  const getMenuById = async () => {
    handleStartLoading();
    const data = await MenuService.GetMenuById(id);
    setMenuData(data);
  };

  const handleUpdateChange = (event) => {
    const files = event.target.files;
    setImages(Array.from(files));
  };

  const handleCreateMenu = () => {
    setShow(true);
  };
  const handleClose = () => {
    setShow(false);
    setImages([]);
  };

  const handleCancelEdit = () => {
    setMenuData(JSON.parse(sessionStorage.getItem('menu')));
    setCanEdit(false);
  };

  const handleTurnOnEdit = () => {
    const session = JSON.stringify(menuData);
    sessionStorage.setItem('menu', session);
    setCanEdit(true);
  };

  const handleEditSubmit = () => {
    handleStartLoading();
    MenuService.EditMenuDetail(id, { detail: menuData.detail })
      .then((res) => {
        setMenuData(res);
        sessionStorage.removeItem('menu');
        setCanEdit(false);
      })
      .catch((err) => enqueueSnackbar(err.response.data, { variant: 'error' }))
      .finally(handleStopLoading);
  };

  const handleCreateSubmit = () => {
    const formData = new FormData();
    images.map((image) => formData.append('menuPage', image));
    handleStartLoading();
    MenuService.CreateMenuDetail(id, formData)
      .then((res) => {
        setMenuData(res);

        handleClose();
        enqueueSnackbar('Thêm thành công', { variant: 'success' });
      })
      .catch((err) => enqueueSnackbar(err.response.data, { variant: 'error' }))
      .finally(handleStopLoading);
  };

  const handleDelete = (id) => {
    setMenuData((prevState) => ({
      ...prevState,
      detail: prevState.detail.filter((menu) => menu._id !== id),
    }));
  };

  const handlePageChange = (e) => {
    const { id, value } = e.target;
    setMenuData((prevState) => ({
      ...prevState,
      detail: prevState.detail.map((menu) => {
        if (menu._id === id) menu.page = value;
        return menu;
      }),
    }));
  };

  const handleTypeClick = (id, type) => {
    setMenuData((prevState) => ({
      ...prevState,
      detail: prevState.detail.map((menu) => {
        if ((menu._id !== id && menu.typePage === type) || (menu._id === id && menu.typePage === type))
          menu.typePage = '';
        else if (menu._id === id) menu.typePage = type;
        return menu;
      }),
    }));
  };

  return (
    <Page title="Menu Detail">
      <Dialog open={show} fullWidth maxWidth="sm" onClose={handleClose}>
        <DialogTitle>
          <Stack>
            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
              <Typography>Menu</Typography>
              <Button variant="contained" component="label">
                Upload
                <input hidden accept="image/*" onChange={handleUpdateChange} multiple type="file" />
              </Button>
            </Box>
            <Typography variant="body2" display="block" sx={{ opacity: '0.6' }}>
              Kích thước: 960x1200
            </Typography>
          </Stack>
        </DialogTitle>
        <DialogContent>
          {images?.map((file) => (
            <Stack direction="row" sx={{ justifyContent: 'space-between', alignContent: 'center' }}>
              <Typography>{file.name}</Typography>
              <IconButton onClick={() => setImages((prevState) => prevState.filter((item) => item.name !== file.name))}>
                <Iconify icon="clarity:window-close-line" />
              </IconButton>
            </Stack>
          ))}
        </DialogContent>
        <DialogActions>
          <Button color="error" variant="outlined" onClick={handleClose}>
            Hủy
          </Button>
          <Button variant={'outlined'} onClick={handleCreateSubmit}>
            Thêm
          </Button>
        </DialogActions>
      </Dialog>

      <Container maxWidth="xl">
        <Stack direction="row" alignItems="center" justifyContent="space-between">
          <Typography variant="h4" gutterBottom>
            {titleData?.menuName}
          </Typography>

          {canEdit ? (
            <Stack direction="row" spacing={1}>
              <Button
                onClick={handleCancelEdit}
                variant="contained"
                startIcon={<Iconify icon="material-symbols:cancel" />}
                color="error"
              >
                Hủy
              </Button>
              <Button
                onClick={handleEditSubmit}
                variant="contained"
                startIcon={<Iconify icon="ant-design:save-filled" />}
              >
                Lưu
              </Button>
            </Stack>
          ) : (
            <Stack direction="row" spacing={1}>
              {menuData?.detail.length > 0 && (
                <Button
                  onClick={handleTurnOnEdit}
                  variant="contained"
                  color="info"
                  startIcon={<Iconify icon="arcticons:huawei-system-update" />}
                >
                  Cập nhật
                </Button>
              )}
              <Button onClick={handleCreateMenu} variant="contained" startIcon={<Iconify icon="akar-icons:plus" />}>
                Thêm trang menu
              </Button>
            </Stack>
          )}
        </Stack>
        <ImageList cols={4} gap={8}>
          {menuData?.detail
            .sort((a, b) => a.page - b.page)
            .map((item) => (
              <ImageListItem key={item?._id} sx={{ minHeight: 200 }}>
                <img src={`${process.env.REACT_APP_IMAGE}${item.image}`} alt={item.title} loading="lazy" />
                <ImageListItemBar
                  title={'Trang'}
                  subtitle={
                    canEdit ? (
                      <InputBase
                        sx={{ color: '#fff', border: '1px solid #fff', borderRadius: '4px', p: '2px 10px' }}
                        id={`${item._id}`}
                        variant={'filled'}
                        name={item._id}
                        value={item.page}
                        onChange={handlePageChange}
                      />
                    ) : (
                      item.page
                    )
                  }
                  actionIcon={
                    canEdit && (
                      <Stack direction={'row'}>
                        <IconButton
                          onClick={() => handleDelete(item._id)}
                          sx={{ color: '#fff', backgroundColor: '#e74c3c', ml: 1 }}
                          aria-label={`Page ${item.page}`}
                        >
                          <Iconify color="#fff" icon="fluent:delete-16-regular" />
                        </IconButton>
                        {menuData.isMobile && (
                          <>
                            <IconButton
                              onClick={() => handleTypeClick(item._id, 'Home')}
                              sx={{ backgroundColor: item?.typePage === 'Home' ? '#0069d9' : '#e3e3e3', ml: 1 }}
                              aria-label={`Page ${item.page}`}
                            >
                              <Iconify
                                color={item?.typePage === 'Home' ? '#fff' : '#000'}
                                icon="material-symbols:home-outline-rounded"
                              />
                            </IconButton>
                            <IconButton
                              onClick={() => handleTypeClick(item._id, 'First')}
                              sx={{ backgroundColor: item?.typePage === 'First' ? '#0069d9' : '#e3e3e3', ml: 1 }}
                              aria-label={`Page ${item.page}`}
                            >
                              <Iconify color={item?.typePage === 'First' ? '#fff' : '#000'} icon="mdi:meat" />
                            </IconButton>
                            <IconButton
                              onClick={() => handleTypeClick(item._id, 'Second')}
                              sx={{ backgroundColor: item?.typePage === 'Second' ? '#0069d9' : '#e3e3e3', ml: 1 }}
                              aria-label={`Page ${item.page}`}
                            >
                              <Iconify
                                color={item?.typePage === 'Second' ? '#fff' : '#000'}
                                icon="arcticons:just-eat"
                              />
                            </IconButton>
                            <IconButton
                              onClick={() => handleTypeClick(item._id, 'Drink')}
                              sx={{ backgroundColor: item?.typePage === 'Drink' ? '#0069d9' : '#e3e3e3', ml: 1 }}
                              aria-label={`Page ${item.page}`}
                            >
                              <Iconify color={item?.typePage === 'Drink' ? '#fff' : '#000'} icon="cil:drink-alcohol" />
                            </IconButton>
                          </>
                        )}
                      </Stack>
                    )
                  }
                />
              </ImageListItem>
            ))}
        </ImageList>
      </Container>
    </Page>
  );
};
export default MenuDetail;
